import type { NodeType } from '@/types/node';

/**
 * What a node type can do. UI branches on capabilities, never on the raw
 * `node.type` string, so a new node type only needs a registry entry.
 */
export interface NodeTypeCapabilities {
  /** Runs nginx: gets generated configs, setup flow (cert + nginx + DNS A/AAAA records) */
  hasNginx: boolean;
  /** Has a control panel login/password stored on the control plane */
  hasPleskCredentials: boolean;
  /** Can manage DNS records (when enabled on the node) */
  supportsDns: boolean;
  /** Exposes a list of hosted domains (cached on the control plane) */
  hasPleskDomains: boolean;
  /** Certificates can be imported from the node's panel */
  importsCertificates: boolean;
  /** Can proxy origin traffic through the local VPN endpoint */
  supportsVpnRouting: boolean;
}

export interface NodeTypeDefinition {
  type: NodeType;
  /** Short label for tables, e.g. "CDN" */
  label: string;
  /** Label for the type picker, e.g. "CDN (nginx)" */
  formLabel: string;
  /** One-line description for the type picker */
  description: string;
  capabilities: NodeTypeCapabilities;
}

/**
 * Registry of node types
 */
export const NODE_TYPES: Record<NodeType, NodeTypeDefinition> = {
  nginx: {
    type: 'nginx',
    label: 'CDN',
    formLabel: 'CDN (nginx)',
    description: 'Edge node with nginx for content delivery',
    capabilities: {
      hasNginx: true,
      hasPleskCredentials: false,
      supportsDns: false,
      hasPleskDomains: false,
      importsCertificates: false,
      supportsVpnRouting: true,
    },
  },
  plesk: {
    type: 'plesk',
    label: 'Plesk',
    formLabel: 'Plesk',
    description: 'Backend server with Plesk panel for DNS/SSL',
    capabilities: {
      hasNginx: false,
      hasPleskCredentials: true,
      supportsDns: true,
      hasPleskDomains: true,
      importsCertificates: true,
      supportsVpnRouting: false,
    },
  },
};

/**
 * Node types in display order
 */
export const NODE_TYPE_LIST: readonly NodeTypeDefinition[] = Object.values(NODE_TYPES);

/**
 * Default type for nodes created before node types existed (and for unknown values)
 */
export const DEFAULT_NODE_TYPE: NodeType = 'nginx';

/**
 * Look up a node type definition; unknown/missing types fall back to the default
 */
export function getNodeType(type: string | null | undefined): NodeTypeDefinition {
  if (type && Object.prototype.hasOwnProperty.call(NODE_TYPES, type)) {
    return NODE_TYPES[type as NodeType];
  }
  return NODE_TYPES[DEFAULT_NODE_TYPE];
}

/**
 * Whether the node's type has a capability
 */
export function nodeHasCapability(
  node: { type?: string | null },
  capability: keyof NodeTypeCapabilities
): boolean {
  return getNodeType(node.type).capabilities[capability];
}
